import { useWatch } from 'react-hook-form';
import { useMemo } from 'react';
import {
  boPhanSearchColumns,
  getHoaDonChungTuSearchColumns,
  khachHangSearchColumns,
  supplierSearchColumns,
  QUERY_KEYS
} from '@/constants';
import { formatDateToYYYYMMDD } from '@/features/tien-gui/so-quy/so-tien-gui-ngan-hang/utils';
import { DateRangeDropdownV2, FormFieldV2, SearchFieldV2 } from '@/components/custom/erp';
import type { BoPhan, KhachHang } from '@/types/schemas';
import { Label } from '@/components/ui/label';

interface BasicInfoTabProps {
  chungTuType: string;
  customer: KhachHang | null;
  boPhan: BoPhan | null;
  hoaDon: any[];
  setCustomer: (customer: KhachHang | null) => void;
  setBoPhan: (boPhan: BoPhan | null) => void;
  setHoaDon: (hoaDon: any[]) => void;
}

const buildInvoiceSearchColumns = () =>
  getHoaDonChungTuSearchColumns().map(column =>
    column.field === ''
      ? {
          ...column,
          field: 'checkbox',
          checkboxSelection: true,
          width: 48,
          minWidth: 48,
          maxWidth: 48,
          renderCell: undefined,
          renderHeader: undefined
        }
      : column
  );

export default function BasicInfoTab({
  chungTuType,
  customer,
  boPhan,
  hoaDon,
  setCustomer,
  setBoPhan,
  setHoaDon
}: BasicInfoTabProps) {
  const [ngay_ct1, ngay_ct2] = useWatch({
    name: ['ngay_ct1', 'ngay_ct2']
  });

  const invoiceSearchColumns = useMemo(() => buildInvoiceSearchColumns(), []);

  const isCustomerType = useMemo(() => ['DHB', 'HDBH', 'HDDV'].includes(chungTuType), [chungTuType]);

  const invoiceSearchEndpoint = useMemo(() => {
    const searchParams = new URLSearchParams({
      type: chungTuType,
      ngay_ct_tu: formatDateToYYYYMMDD(ngay_ct1),
      ngay_ct_den: formatDateToYYYYMMDD(ngay_ct2),
      customer: customer?.uuid || ''
    });

    if (boPhan?.uuid) {
      searchParams.set('ma_bp', boPhan.uuid);
    }

    return `/invoice-with-filter/?${searchParams.toString()}`;
  }, [boPhan?.uuid, chungTuType, customer?.uuid, ngay_ct1, ngay_ct2]);

  return (
    <div className='space-y-3 p-4'>
      <div className='flex items-center gap-2'>
        <Label className='w-40 shrink-0'>Ngày c/từ (từ/đến)</Label>
        <DateRangeDropdownV2
          fromDateName='ngay_ct1'
          toDateName='ngay_ct2'
          fromFieldClassName='w-48'
          toFieldClassName='w-48'
        />
      </div>

      <div className='flex flex-wrap items-center gap-2 xl:flex-nowrap'>
        <Label className='w-40 min-w-40 font-medium'>{isCustomerType ? 'Mã khách hàng' : 'Mã nhà cung cấp'}</Label>
        <SearchFieldV2<KhachHang>
          value={customer?.customer_code || ''}
          relatedFieldValue={customer?.customer_name || ''}
          searchEndpoint={isCustomerType ? `/${QUERY_KEYS.KHACH_HANG}/` : `/${QUERY_KEYS.NHA_CUNG_CAP}/`}
          searchColumns={khachHangSearchColumns}
          columnDisplay='customer_code'
          displayRelatedField='customer_name'
          dialogTitle={isCustomerType ? 'Danh mục khách hàng' : 'Danh mục nhà cung cấp'}
          onRowSelection={setCustomer}
          className='w-100'
        />
      </div>

      <div className='flex flex-wrap items-center gap-2 xl:flex-nowrap'>
        <Label className='w-40 min-w-40 font-medium'>Mã bộ phận</Label>
        <SearchFieldV2<BoPhan>
          value={boPhan?.ma_bp || ''}
          relatedFieldValue={boPhan?.ten_bp || ''}
          searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
          searchColumns={boPhanSearchColumns}
          columnDisplay='ma_bp'
          displayRelatedField='ten_bp'
          dialogTitle='Danh mục bộ phận'
          onRowSelection={setBoPhan}
          className='w-100'
        />
      </div>

      <div className='flex flex-wrap items-center gap-2 xl:flex-nowrap'>
        <Label className='w-40 min-w-40 font-medium'>Chứng từ</Label>
        <SearchFieldV2<any>
          multiple
          value={hoaDon}
          searchEndpoint={invoiceSearchEndpoint}
          searchColumns={invoiceSearchColumns as any}
          columnDisplay='so_ct'
          dialogTitle='Chọn chứng từ'
          onRowSelection={setHoaDon}
          className='w-100'
          disabled={!customer}
        />
      </div>

      <div className='flex flex-wrap items-center gap-2 xl:flex-nowrap'>
        <Label className='w-40 min-w-40 font-medium'>Xử lý</Label>
        <FormFieldV2
          name='xprocess'
          type='select'
          className='w-full max-w-[340px] xl:max-w-[420px]'
          options={[
            { label: 'Thêm vào chi tiết', value: '1' },
            { label: 'Xóa hết chi tiết và thêm mới', value: '2' }
          ]}
        />
      </div>
    </div>
  );
}
